import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import CardGrid from '../components/CardGrid';
import SearchBar from '../components/SearchBar';
import FilterDrawer from '../components/FilterDrawer';
import Pagination from '../components/Pagination';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './CardBrowser.css';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import AddToWishListModal from '../components/AddToWishListModal';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

export interface Card {
  cardId: string;
  name: string;
  setCode: string;
  setName: string;
  rarity: string;
  manaCost: string;
  typeLine: string;
  colors: string;
  layoutCode: string;
  imageSmall: string;
  imageUrl: string;
  imageLarge: string;
  hasLocalImage: boolean;
  backImageSmall: string;
  backImageUrl: string;
  backImageLarge: string;
  price: number;
  qtyOwned:        number;
  qtyOwnedRegular: number;
  qtyOwnedFoil:    number;
  isFoilOwned?:    boolean;
  borderColor?:    string;
  frameEffects?:   string[];
  finishes?:       string[];
  promoTypes?:     string[];
}

export interface SetOption {
  setCode: string;
  name: string;
  setType: string;
  releasedAt: string;
}

export interface Filters {
  colors: string[];
  rarities: string[];
  typeLine: string;
  setCodes: string[];
  format: string;
  keywords: string[];
  tags: string[];
  produces: string[];
  ownedOnly: boolean;
  showTokens: boolean;
  showDigital: boolean;
  showUnsets: boolean;
  showArtSeries: boolean;
  showNoiseSetTypes: boolean;
  minPrice: number;
  maxPrice: number;
  sortBy: string;
  // Colour / produces extras
  colorless: boolean;
  producesColorless: boolean;
  // CMC range
  cmcMin: number;
  cmcMinOp: string;   // 'gte' | 'gt' | 'eq' | 'lte' | 'lt'
  cmcMax: number;
  cmcMaxOp: string;
  // Pip builder
  pipsW: number;
  pipsU: number;
  pipsB: number;
  pipsR: number;
  pipsG: number;
  pipsC: number;
  pipsGeneric: number;
  pipsX: string;      // '' | 'X' | 'XX' | 'XXX'
  pipMode: string;    // 'all' | 'any'
  pipCount: string;   // 'atleast' | 'exact'
  // Release year range
  releaseYearFrom: number;
  releaseYearTo: number;
}

export type GridSize = 'sm' | 'md' | 'lg' | 'xl';

function parseQuery(q: string): { name?: string; oracle?: string; flavour?: string; artist?: string } {
  const lower = q.trim().toLowerCase();
  if (lower.startsWith('o:') && q.slice(2).trim().length > 0) return { oracle: q.slice(2).trim() };
  if (lower.startsWith('f:') && q.slice(2).trim().length > 0) return { flavour: q.slice(2).trim() };
  if (lower.startsWith('a:') && q.slice(2).trim().length > 0) return { artist: q.slice(2).trim() };
  const name = q.trim();
  return name.length > 0 ? { name } : {};
}

// ─── DEFAULT FILTERS ─────────────────────────────────────────────────────────

const EMPTY_FILTERS: Omit<Filters, 'sortBy' | 'format' | 'ownedOnly'> = {
  colors: [], rarities: [], typeLine: '',
  setCodes: [], keywords: [], tags: [], produces: [],
  showTokens: false, showDigital: false,
  showUnsets: false, showArtSeries: false, showNoiseSetTypes: false,
  minPrice: 0, maxPrice: 0,
  colorless: false, producesColorless: false,
  cmcMin: 0, cmcMinOp: 'gte', cmcMax: 0, cmcMaxOp: 'lte',
  pipsW: 0, pipsU: 0, pipsB: 0, pipsR: 0, pipsG: 0, pipsC: 0,
  pipsGeneric: 0, pipsX: '',
  pipMode: 'all', pipCount: 'atleast',
  releaseYearFrom: 0, releaseYearTo: 0,
};

// ─── ACTIVE FILTER BADGES ────────────────────────────────────────────────────

const COLOR_NAMES: Record<string, string> = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green' };
const RARITY_NAMES: Record<string, string> = { mythic: 'Mythic', rare: 'Rare', uncommon: 'Uncommon', common: 'Common' };

function pipSummary(f: Filters): string {
  const parts: string[] = [];
  const syms: [keyof Filters, string][] = [
    ['pipsW','W'],['pipsU','U'],['pipsB','B'],['pipsR','R'],['pipsG','G'],['pipsC','C']
  ];
  syms.forEach(([k, label]) => {
    const v = f[k] as number;
    if (v > 0) parts.push(label.repeat(v));
  });
  if (f.pipsGeneric > 0) parts.push(`${f.pipsGeneric}gen`);
  return parts.join(' ');
}

function hasPips(f: Filters): boolean {
  return f.pipsW > 0 || f.pipsU > 0 || f.pipsB > 0 ||
         f.pipsR > 0 || f.pipsG > 0 || f.pipsC > 0 || f.pipsGeneric > 0;
}

function clearPips(f: Filters): Filters {
  return { ...f, pipsW:0, pipsU:0, pipsB:0, pipsR:0, pipsG:0, pipsC:0, pipsGeneric:0, pipsX:'' };
}

function ActiveFilterBadges({
  query, filters, onRemoveQuery, onUpdateFilters, onClearAll
}: {
  query: string;
  filters: Filters;
  onRemoveQuery: () => void;
  onUpdateFilters: (f: Filters) => void;
  onClearAll: () => void;
}) {
  const badges: { key: string; label: string; onRemove: () => void }[] = [];

  // Search query
  if (query.trim()) {
    badges.push({ key: 'query', label: `"${query}"`, onRemove: onRemoveQuery });
  }

  // Format
  if (filters.format) {
    badges.push({ key: 'format', label: `Format: ${filters.format}`,
      onRemove: () => onUpdateFilters({ ...filters, format: '' }) });
  }

  // Colors
  filters.colors.forEach(c => badges.push({
    key: `color-${c}`, label: `Color: ${COLOR_NAMES[c] || c}`,
    onRemove: () => onUpdateFilters({ ...filters, colors: filters.colors.filter(x => x !== c) })
  }));

  // Colorless
  if (filters.colorless) {
    badges.push({ key: 'colorless', label: 'Colorless',
      onRemove: () => onUpdateFilters({ ...filters, colorless: false }) });
  }

  // Rarities
  filters.rarities.forEach(r => badges.push({
    key: `rarity-${r}`, label: `Rarity: ${RARITY_NAMES[r] || r}`,
    onRemove: () => onUpdateFilters({ ...filters, rarities: filters.rarities.filter(x => x !== r) })
  }));

  // Type line
  if (filters.typeLine) {
    badges.push({ key: 'typeLine', label: `Type: ${filters.typeLine}`,
      onRemove: () => onUpdateFilters({ ...filters, typeLine: '' }) });
  }

  // Sets
  filters.setCodes.forEach(sc => badges.push({
    key: `set-${sc}`, label: `Set: ${sc.toUpperCase()}`,
    onRemove: () => onUpdateFilters({ ...filters, setCodes: filters.setCodes.filter(x => x !== sc) })
  }));

  // Keywords
  filters.keywords.forEach(kw => badges.push({
    key: `kw-${kw}`, label: `Keyword: ${kw}`,
    onRemove: () => onUpdateFilters({ ...filters, keywords: filters.keywords.filter(x => x !== kw) })
  }));

  // Tags
  (filters.tags || []).forEach(tag => badges.push({
    key: `tag-${tag}`, label: `Tag: ${tag}`,
    onRemove: () => onUpdateFilters({ ...filters, tags: (filters.tags || []).filter(x => x !== tag) })
  }));

  // Produces
  (filters.produces || []).forEach(c => badges.push({
    key: `produces-${c}`, label: `Produces: ${COLOR_NAMES[c] || c}`,
    onRemove: () => onUpdateFilters({ ...filters, produces: (filters.produces || []).filter(x => x !== c) })
  }));
  if (filters.producesColorless) {
    badges.push({ key: 'producesC', label: 'Produces: Colorless',
      onRemove: () => onUpdateFilters({ ...filters, producesColorless: false }) });
  }

  // Owned only
  if (filters.ownedOnly) {
    badges.push({ key: 'ownedOnly', label: 'Owned Only',
      onRemove: () => onUpdateFilters({ ...filters, ownedOnly: false }) });
  }

  // CMC
  if (filters.cmcMin > 0 || filters.cmcMax > 0) {
    const opLabel: Record<string,string> = { gte:'≥', gt:'>', eq:'=', lte:'≤', lt:'<' };
    let label = 'CMC';
    if (filters.cmcMin > 0) label += ` ${opLabel[filters.cmcMinOp] || '≥'}${filters.cmcMin}`;
    if (filters.cmcMin > 0 && filters.cmcMax > 0) label += ' and';
    if (filters.cmcMax > 0) label += ` ${opLabel[filters.cmcMaxOp] || '≤'}${filters.cmcMax}`;
    badges.push({ key: 'cmc', label,
      onRemove: () => onUpdateFilters({ ...filters, cmcMin: 0, cmcMax: 0 }) });
  }

  // Pips
  if (hasPips(filters)) {
    badges.push({ key: 'pips', label: `Pips: ${pipSummary(filters)}`,
      onRemove: () => onUpdateFilters(clearPips(filters)) });
  }

  // X cost
  if (filters.pipsX) {
    badges.push({ key: 'pipsX', label: `X cost: ${filters.pipsX}`,
      onRemove: () => onUpdateFilters({ ...filters, pipsX: '' }) });
  }

  // Release year
  if (filters.releaseYearFrom > 0 || filters.releaseYearTo > 0) {
    const from = filters.releaseYearFrom || '…';
    const to   = filters.releaseYearTo   || '…';
    badges.push({ key: 'releaseYear', label: `Released: ${from}–${to}`,
      onRemove: () => onUpdateFilters({ ...filters, releaseYearFrom: 0, releaseYearTo: 0 }) });
  }

  // Price
  if (filters.minPrice > 0) {
    badges.push({ key: 'minPrice', label: `Min $${filters.minPrice}`,
      onRemove: () => onUpdateFilters({ ...filters, minPrice: 0 }) });
  }
  if (filters.maxPrice > 0) {
    badges.push({ key: 'maxPrice', label: `Max $${filters.maxPrice}`,
      onRemove: () => onUpdateFilters({ ...filters, maxPrice: 0 }) });
  }

  // Show toggles
  if (filters.showTokens)        badges.push({ key: 'tokens',  label: 'Tokens',      onRemove: () => onUpdateFilters({ ...filters, showTokens: false }) });
  if (filters.showDigital)       badges.push({ key: 'digital', label: 'Digital',     onRemove: () => onUpdateFilters({ ...filters, showDigital: false }) });
  if (filters.showUnsets)        badges.push({ key: 'unsets',  label: 'Un-sets',     onRemove: () => onUpdateFilters({ ...filters, showUnsets: false }) });
  if (filters.showArtSeries)     badges.push({ key: 'art',     label: 'Art Series',  onRemove: () => onUpdateFilters({ ...filters, showArtSeries: false }) });
  if (filters.showNoiseSetTypes) badges.push({ key: 'promos',  label: 'Promos etc.', onRemove: () => onUpdateFilters({ ...filters, showNoiseSetTypes: false }) });

  if (badges.length === 0) return null;

  return (
    <div className="active-filter-badges">
      {badges.map(b => (
        <span key={b.key} className="active-filter-badge">
          {b.label}
          <button className="active-filter-badge-remove" onClick={b.onRemove} title="Remove filter">✕</button>
        </span>
      ))}
      {badges.length > 1 && (
        <button className="active-filter-clear-all" onClick={onClearAll}>Clear all</button>
      )}
    </div>
  );
}

export default function CardBrowser() {
  const { prefs } = useAuth();
  const location = useLocation();

  // Restore state from sessionStorage if navigating back from card detail
  const savedState = (() => {
    try {
      const s = sessionStorage.getItem('cardBrowserState');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  })();

  // setCodes passed via URL (e.g. from Sets page "View Cards in Set") take
  // priority over sessionStorage — it's an explicit fresh navigation intent.
  const urlSetCodes = (() => {
    const raw = new URLSearchParams(location.search).get('setCodes');
    return raw ? raw.split(',').map(c => c.trim().toLowerCase()).filter(Boolean) : null;
  })();

  const [query,            setQuery]            = useState(urlSetCodes ? '' : (savedState?.query ?? ''));
  const [cards,            setCards]            = useState<Card[]>([]);
  const [filters,          setFilters]          = useState<Filters>(() => {
    const prefDefaults: Filters = {
      ...EMPTY_FILTERS,
      format:   prefs.defaultCollectionFormat || '',
      ownedOnly: prefs.ownedOnlyDefault ?? false,
      sortBy:   'name',
    };
    if (urlSetCodes && urlSetCodes.length > 0) {
      return { ...prefDefaults, setCodes: urlSetCodes, ownedOnly: false, format: '' };
    }
    if (!savedState?.filters) return prefDefaults;
    const saved = savedState.filters as Filters;
    return {
      ...prefDefaults,
      ...saved,
      format: saved.format !== undefined ? saved.format : prefDefaults.format,
    };
  });
  const [sets,             setSets]             = useState<SetOption[]>([]);
  const [loading,          setLoading]          = useState(false);
  const [total,            setTotal]            = useState(0);
  const [totalPages,       setTotalPages]       = useState(0);
  const [page,             setPage]             = useState(0);
  const [limit,            setLimit]            = useState(24);
  const [gridSize,         setGridSize]         = useState<GridSize>(savedState?.gridSize || (prefs.defaultGridSize as GridSize) || 'md');
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number; items: ContextMenuItem[];
  } | null>(null);
  const [wishListModal, setWishListModal] = useState<{
    cardId: string; cardName: string; imageUrl?: string | null;
  } | null>(null);
  const [pinned,           setPinned]           = useState(false);
  const [showAllPrintings, setShowAllPrintings] = useState(
    urlSetCodes ? true : (savedState?.showAllPrintings ?? prefs.showAllPrintingsDefault ?? false)
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef    = useRef<AbortController | null>(null);

  // Clear stale sessionStorage if it predates prefs integration
  useEffect(() => {
    try {
      const s = sessionStorage.getItem('cardBrowserState');
      if (s) {
        const parsed = JSON.parse(s);
        if (!parsed.filters?.format && prefs.defaultCollectionFormat) {
          sessionStorage.removeItem('cardBrowserState');
        }
      }
    } catch { sessionStorage.removeItem('cardBrowserState'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist browser state to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('cardBrowserState', JSON.stringify({
        query, filters, gridSize, showAllPrintings
      }));
    } catch { /* ignore */ }
  }, [query, filters, gridSize, showAllPrintings]);

  // Re-apply setCodes from URL on every navigation (not just first mount) —
  // SPA route changes don't remount the component, so useState initialisers
  // alone won't catch a second visit with a different ?setCodes= value.
  useEffect(() => {
    const raw = new URLSearchParams(location.search).get('setCodes');
    if (!raw) return;
    const codes = raw.split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
    if (codes.length === 0) return;
    setQuery('');
    setFilters(f => ({ ...f, setCodes: codes, ownedOnly: false, format: '' }));
    setShowAllPrintings(true);
    setPage(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    axios.get(`${API}/sets`).then(res => setSets(res.data)).catch(() => {});
  }, []);

  const fetchCards = useCallback(async (
    q: string, f: Filters, pg: number, lim: number, allPrintings: boolean = false
  ) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setLoading(true);
    try {
      const parsed = parseQuery(q);
      const params: Record<string, string | number | boolean> = {
        limit: lim, page: pg,
        sortBy:            f.sortBy,
        ownedOnly:         f.ownedOnly,
        showTokens:        f.showTokens,
        showDigital:       f.showDigital,
        showUnsets:        f.showUnsets,
        showArtSeries:     f.showArtSeries,
        showNoiseSetTypes: f.showNoiseSetTypes,
        showAllPrintings:  allPrintings,
      };
      if (parsed.name)    params.q       = parsed.name;
      if (parsed.oracle)  params.oracle  = parsed.oracle;
      if (parsed.flavour) params.flavour = parsed.flavour;
      if (parsed.artist)  params.artist  = parsed.artist;

      // Set codes from drawer only
      if (f.setCodes.length > 0) params.setCodes = f.setCodes.join(',');

      if (f.colors.length > 0)   params.colors   = f.colors.join(',');
      if (f.rarities.length > 0) params.rarities = f.rarities.join(',');
      if (f.typeLine.trim())      params.typeLine = f.typeLine;
      if (f.format.trim())        params.format   = f.format;
      if (f.keywords.length > 0)  params.keywords = f.keywords.join(',');
      if ((f.tags || []).length > 0) params.tags  = (f.tags || []).join(',');
      if (f.minPrice > 0)         params.minPrice = f.minPrice;
      if (f.maxPrice > 0)         params.maxPrice = f.maxPrice;
      if (f.produces && f.produces.length > 0) params.produces = f.produces.join(',');

      // ── New mana cost / CMC / pip / release year params ──────────────────
      if (f.colorless)          params.colorless         = true;
      if (f.producesColorless)  params.producesColorless = true;
      if (f.cmcMin > 0)         params.cmcMin            = f.cmcMin;
      if (f.cmcMin > 0)         params.cmcMinOp          = f.cmcMinOp || 'gte';
      if (f.cmcMax > 0)         params.cmcMax            = f.cmcMax;
      if (f.cmcMax > 0)         params.cmcMaxOp          = f.cmcMaxOp || 'lte';
      if (f.pipsW > 0)          params.pipsW             = f.pipsW;
      if (f.pipsU > 0)          params.pipsU             = f.pipsU;
      if (f.pipsB > 0)          params.pipsB             = f.pipsB;
      if (f.pipsR > 0)          params.pipsR             = f.pipsR;
      if (f.pipsG > 0)          params.pipsG             = f.pipsG;
      if (f.pipsC > 0)          params.pipsC             = f.pipsC;
      if (f.pipsGeneric > 0)    params.pipsGeneric       = f.pipsGeneric;
      if (f.pipsX)              params.pipsX             = f.pipsX;
      if (f.pipMode && f.pipMode !== 'all')   params.pipMode   = f.pipMode;
      if (f.pipCount && f.pipCount !== 'atleast') params.pipCount = f.pipCount;
      if (f.releaseYearFrom > 0) params.releaseYearFrom  = f.releaseYearFrom;
      if (f.releaseYearTo > 0)   params.releaseYearTo    = f.releaseYearTo;

      const res = await axios.get(`${API}/cards/search`, { params, signal });
      if (!signal.aborted) {
        setCards(res.data.cards);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      }
    } catch (e: any) {
      if (e?.code !== 'ERR_CANCELED') console.error('Search failed', e);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      fetchCards(query, filters, 0, limit, showAllPrintings);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, filters, limit, showAllPrintings, fetchCards]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchCards(query, filters, newPage, limit, showAllPrintings);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(0);
  };

  const SIZE_OPTIONS: { size: GridSize; icon: string; label: string }[] = [
    { size: 'sm', icon: '⊞', label: 'Small' },
    { size: 'md', icon: '▣', label: 'Medium' },
    { size: 'lg', icon: '□', label: 'Large' },
    { size: 'xl', icon: '▢', label: 'Extra Large' },
  ];

  const handleCardContextMenu = (card: Card, e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: '🔍',
          label: 'View Card Detail',
          onClick: () => window.location.assign(`/cards/${card.cardId}`),
        },
        {
          icon: '📋',
          label: 'Add to Wish List',
          onClick: () => setWishListModal({ cardId: card.cardId, cardName: card.name, imageUrl: card.imageUrl || card.imageSmall || null }),
        },
      ],
    });
  };

  return (
    <div className={`card-browser ${pinned ? 'drawer-pinned' : ''}`}>
      <FilterDrawer
        filters={filters}
        sets={sets}
        onChange={setFilters}
        onPinChange={setPinned}
      />
      <div className="browser-content">
        <div className="browser-header">
          <div className="browser-title">
            <h1>Card Browser</h1>
          </div>
          {total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              compact
            />
          )}
          <div className="browser-header-right">
            <SearchBar
              value={query}
              onChange={(v: string) => setQuery(v)}
              onSetCode={(code: string) => {
                if (code.length > 0 && !filters.setCodes.includes(code)) {
                  setFilters(f => ({ ...f, setCodes: [...f.setCodes, code] }));
                }
                setQuery('');
              }}
              placeholder="Quick Search — name, o: oracle, f: flavour, a: artist, s: set"
            />
            <button
              className={`printings-toggle ${showAllPrintings && !filters.ownedOnly ? 'active' : ''} ${filters.ownedOnly ? 'disabled' : ''}`}
              onClick={() => { if (!filters.ownedOnly) setShowAllPrintings((v: boolean) => !v); }}
              title={filters.ownedOnly ? 'Owned Only shows all your printings automatically — disable Owned Only to use this filter' : showAllPrintings ? 'Showing all printings' : 'Showing one per card'}
              style={filters.ownedOnly ? { opacity: 0.35, cursor: 'not-allowed' } : {}}>
              ⊞ All Printings
            </button>
            <select
              className="sort-select"
              value={filters.sortBy}
              onChange={e => setFilters(f => ({ ...f, sortBy: e.target.value }))}
              title="Sort order"
            >
              <option value="name">Name A–Z</option>
              <option value="name_desc">Name Z–A</option>
              <option value="price_desc">Price ↓</option>
              <option value="price_asc">Price ↑</option>
              <option value="rarity">Rarity</option>
              <option value="released_desc">Print Date ↓</option>
              <option value="released_asc">Print Date ↑</option>
              <option value="artist">Artist A–Z</option>
              <option value="set">Set A–Z</option>
            </select>
            <div className="grid-size-toggle">
              {SIZE_OPTIONS.map(({ size, icon, label }) => (
                <button
                  key={size}
                  className={`size-btn ${gridSize === size ? 'active' : ''}`}
                  onClick={() => setGridSize(size)}
                  title={label}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>
        <ActiveFilterBadges
          query={query}
          filters={filters}
          onRemoveQuery={() => setQuery('')}
          onUpdateFilters={setFilters}
          onClearAll={() => {
            setQuery('');
            setFilters({
              ...EMPTY_FILTERS,
              sortBy: filters.sortBy,
              format: '',
              ownedOnly: false,
            });
          }}
        />
        <div className="browser-main">
          {loading && (
            <div className="loading-overlay">
              <div className="loading-spinner" />
            </div>
          )}
          <CardGrid cards={cards} gridSize={gridSize} onCardContextMenu={handleCardContextMenu} suppressHover={!!contextMenu} />
          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </div>
      </div>
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
      {wishListModal && (
        <AddToWishListModal
          cardId={wishListModal.cardId}
          cardName={wishListModal.cardName}
          imageUrl={wishListModal.imageUrl}
          onDone={() => setWishListModal(null)}
          onCancel={() => setWishListModal(null)}
        />
      )}
    </div>
  );
}
